import { useEffect } from 'react';
import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { isValidLocale, DEFAULT_LOCALE, type Locale } from '@/i18n/types';

/**
 * LocaleRedirect - Handles automatic locale detection and redirect
 * Placed at the root "/" to redirect to the correct locale
 */
export function LocaleDetector() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only redirect from exact root
    if (location.pathname !== '/') return;

    // Check saved preference
    let savedLocale: string | null = null;
    try {
      savedLocale = localStorage.getItem('punto-hispano-locale');
    } catch {}

    if (savedLocale && isValidLocale(savedLocale)) {
      // User has an explicit preference — respect it (including default 'es')
      if (savedLocale !== DEFAULT_LOCALE) {
        navigate(`/${savedLocale}`, { replace: true });
      }
      return;
    }

    // Browser language auto-detection disabled — always default to Spanish.
    // Users can switch language manually via the selector; that choice is
    // saved in localStorage and respected on subsequent visits.
  }, [location.pathname, navigate]);

  return null;
}

/**
 * LocaleGuard - Validates the locale param and redirects if invalid
 */
export function LocaleGuard({ children }: { children: React.ReactNode }) {
  const { locale } = useParams<{ locale: string }>();
  
  if (locale && !isValidLocale(locale)) {
    // Not a valid locale - this isn't a locale route, let it fall through
    return <Navigate to="/404" replace />;
  }

  return <>{children}</>;
}
